import { useEffect, useState } from 'react';
import { trucksAPI, authAPI } from '../api';
import { RotateCcw } from 'lucide-react';
import { Check, Clock, AlertTriangle, RefreshCw, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const Trucks = () => {
  const [trucks, setTrucks] = useState([]);
  const [stats, setStats] = useState({ available: 0, in_use: 0, issues: 0 });
  const [logs, setLogs] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogs, setShowLogs] = useState(false);
  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [missingItemsModalOpen, setMissingItemsModalOpen] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [repairData, setRepairData] = useState({
    issue_type: 'Vehicle blew up',
    description: '',
  });
  const [missingItems, setMissingItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const issueTypes = [
    'Vehicle blew up',
    'Vehicle smoking or on fire',
    'Engine failure',
    'Brake issues',
    'Tire blowout',
    'Transmission failure',
    'Other',
  ];

  const inventoryItems = [
    'Cones',
    'Repair kit',
    'Tire repair kit',
    'Fire extinguisher',
    "5x Mike's Jerky",
  ];

  const loadData = async () => {
    try {
      const [trucksRes, statsRes, userRes] = await Promise.all([
        trucksAPI.getTrucks(),
        trucksAPI.getTruckStats(),
        authAPI.getUserProfile(),
      ]);
      setTrucks(trucksRes.data);
      setStats(statsRes.data);
      setUser(userRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load trucks');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const logsRes = await trucksAPI.getTruckLogs();
      setLogs(logsRes.data);
    } catch (err) {
      console.error('Failed to load logs:', err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (showLogs && user) {
      loadLogs();
    }
  }, [showLogs, user]);

  const handleUseTruck = async (truck) => {
    if (truck.status === 'in_use') {
      if (truck.current_user_id === user.id) {
        if (window.confirm('Return this truck?')) {
          try {
            await trucksAPI.returnTruck(truck.id);
            toast.success('Truck returned!');
            loadData();
          } catch (err) {
            toast.error('Failed to return truck');
          }
        }
      } else {
        toast.error(`In use by ${truck.current_user_name}`);
      }
    } else {
      try {
        await trucksAPI.useTruck(truck.id);
        toast.success('Truck checked out!');
        loadData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed');
      }
    }
  };

  const handleOverrideReturn = async (truck) => {
    if (!window.confirm(`Override return for "${truck.name}"? (checked out by ${truck.current_user_name})`)) return;
    try {
      await trucksAPI.overrideReturn(truck.id);
      toast.success(`Override: "${truck.name}" returned.`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Override failed');
    }
  };

  const openRepairModal = (truck) => {
    setSelectedTruck(truck);
    setRepairModalOpen(true);
    setRepairData({ issue_type: 'Vehicle blew up', description: '' });
  };

  const openMissingItemsModal = (truck) => {
    setSelectedTruck(truck);
    setMissingItemsModalOpen(true);
    setMissingItems([]);
  };

  const handleRepairSubmit = async () => {
    if (!selectedTruck) return;
    setSubmitting(true);
    try {
      await trucksAPI.reportRepair(selectedTruck.id, repairData);
      toast.success('Repair report submitted!');
      setRepairModalOpen(false);
      setRepairData({ issue_type: 'Vehicle blew up', description: '' });
      loadData();
    } catch (err) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMissingItemsSubmit = async () => {
    if (!selectedTruck || missingItems.length === 0) {
      toast.error('Select at least one item');
      return;
    }
    setSubmitting(true);
    try {
      await trucksAPI.reportMissingItems(selectedTruck.id, { missing_items: missingItems });
      toast.success('Missing items report submitted!');
      setMissingItemsModalOpen(false);
      setMissingItems([]);
      loadData();
    } catch (err) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMissingItem = (item) => {
    setMissingItems((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const MGMT_ROLE_IDS = new Set(['1179346546748301325','1179346546748301323','1401324758749941851','1316884514768687164']);
  const userRoleIds   = new Set(user?.roles ?? []);
  const canViewLogs   = [...userRoleIds].some(id => MGMT_ROLE_IDS.has(id));
  // Override button is shown to all logged-in users on trucks checked out by others.
  // The backend is the authoritative gate — it checks live Discord roles and returns 403 if not allowed.
  const canOverride   = !!user;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-8 px-6 flex items-center justify-center">
        <div className="text-[#71717a] text-xs uppercase">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-8 px-6 bg-[#0a0a0c]">
      <div className="max-w-7xl mx-auto">

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold uppercase mb-1">Fleet Status</h1>
            <p className="text-[#71717a] text-xs">View and manage vehicles</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 bg-[#18181b] hover:bg-[#27272a] text-xs uppercase"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
            {canViewLogs && (
              <button
                onClick={() => setShowLogs(!showLogs)}
                className="flex items-center gap-2 px-4 py-2 bg-[#8b5cf6] hover:bg-[#7c3aed] text-xs uppercase"
              >
                <FileText className="w-3 h-3" />
                {showLogs ? 'Hide' : 'Logs'}
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-[#18181b] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-4 h-4 text-[#10b981]" strokeWidth={3} />
              <span className="text-xs uppercase text-[#71717a]">Available</span>
            </div>
            <div className="text-3xl font-bold mono-number">{stats.available}</div>
          </div>

          <div className="bg-[#18181b] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[#eab308]" strokeWidth={3} />
              <span className="text-xs uppercase text-[#71717a]">In Use</span>
            </div>
            <div className="text-3xl font-bold mono-number">{stats.in_use}</div>
          </div>

          <div className="bg-[#18181b] p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-[#ef4444]" strokeWidth={3} />
              <span className="text-xs uppercase text-[#71717a]">Issues</span>
            </div>
            <div className="text-3xl font-bold mono-number">{stats.issues}</div>
          </div>
        </div>

        {showLogs && canViewLogs ? (
          <div className="bg-[#18181b] p-4">
            <h2 className="text-lg font-bold uppercase mb-4">Usage Logs</h2>
            {logs.length === 0 ? (
              <div className="text-[#71717a] text-sm">No logs yet</div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {logs.map((log) => (
                  <div key={log.id} className="flex items-center justify-between p-3 bg-[#0a0a0c] text-sm">
                    <div>
                      <span className="font-bold">{log.truck_name}</span>
                      <span className="text-[#71717a] mx-2">•</span>
                      <span className={log.action === 'checked_out' ? 'text-[#eab308]' : 'text-[#10b981]'}>
                        {log.action === 'checked_out' ? 'OUT' : 'RETURNED'}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs">{log.driver_name}</div>
                      <div className="text-xs text-[#71717a]">{new Date(log.timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-4">
            {trucks.map((truck) => (
              <div
                key={truck.id}
                className="bg-[#18181b] overflow-hidden"
              >
                <div className="relative">
                  <img
                    src="/front3.png"
                    alt="Phantom Truck"
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute top-2 right-2">
                    {truck.status === 'available' ? (
                      <Check className="w-5 h-5 text-[#10b981]" strokeWidth={3} />
                    ) : (
                      <Clock className="w-5 h-5 text-[#eab308]" strokeWidth={3} />
                    )}
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-sm font-bold uppercase mb-1">{truck.name}</h3>
                  <p className="text-xs text-[#71717a] mb-4">{truck.number}</p>

                  <div className="space-y-2">
                    {/* Primary: Use / Return (own truck) / In Use label */}
                    {truck.status === 'available' ? (
                      <button
                        onClick={() => handleUseTruck(truck)}
                        className="w-full px-3 py-2 text-xs font-bold uppercase bg-[#eab308] hover:bg-[#ca8a04] text-black"
                      >
                        Use
                      </button>
                    ) : truck.current_user_id === user?.id ? (
                      <button
                        onClick={() => handleUseTruck(truck)}
                        className="w-full px-3 py-2 text-xs font-bold uppercase bg-[#10b981] hover:bg-[#059669] text-black"
                      >
                        Return
                      </button>
                    ) : (
                      <>
                        <div className="w-full px-3 py-2 text-xs font-bold uppercase bg-[#27272a] text-[#71717a] text-center">
                          In Use — {truck.current_user_name || '?'}
                        </div>
                        {/* Override Return — Senior Operator+ only */}
                        {canOverride && (
                          <button
                            onClick={() => handleOverrideReturn(truck)}
                            className="w-full px-3 py-2 text-xs font-bold uppercase bg-[#7c3aed] hover:bg-[#6d28d9] text-white flex items-center justify-center gap-1.5"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Override Return
                          </button>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => openRepairModal(truck)}
                      className="w-full px-3 py-2 bg-[#27272a] hover:bg-[#3f3f46] text-white text-xs font-bold uppercase"
                    >
                      Report
                    </button>
                    <button
                      onClick={() => openMissingItemsModal(truck)}
                      className="w-full px-3 py-2 bg-[#27272a] hover:bg-[#3f3f46] text-white text-xs font-bold uppercase"
                    >
                      Missing Items
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Repair Modal */}
      <Dialog open={repairModalOpen} onOpenChange={setRepairModalOpen}>
        <DialogContent className="bg-[#111114] border border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Request Repair</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Report issue with {selectedTruck?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-medium mb-2 block">Issue Type</Label>
              <div className="space-y-1">
                {issueTypes.map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="issue_type"
                      value={type}
                      checked={repairData.issue_type === type}
                      onChange={(e) => setRepairData({ ...repairData, issue_type: e.target.value })}
                      className="w-3 h-3"
                    />
                    <span className="text-slate-300">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="description" className="text-xs font-medium mb-2 block">
                Details
              </Label>
              <Textarea
                id="description"
                placeholder="Additional info..."
                value={repairData.description}
                onChange={(e) => setRepairData({ ...repairData, description: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-sm"
                rows={3}
              />
            </div>

            <button
              onClick={handleRepairSubmit}
              disabled={submitting}
              className="btn-primary w-full text-xs"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Missing Items Modal */}
      <Dialog open={missingItemsModalOpen} onOpenChange={setMissingItemsModalOpen}>
        <DialogContent className="bg-[#111114] border border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Report Missing Items</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Select items missing from {selectedTruck?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-medium mb-2 block">Required Items</Label>
              <div className="space-y-2">
                {inventoryItems.map((item) => (
                  <label
                    key={item}
                    className="flex items-center gap-3 cursor-pointer p-2 bg-zinc-900 hover:bg-zinc-800"
                  >
                    <Checkbox
                      checked={missingItems.includes(item)}
                      onCheckedChange={() => toggleMissingItem(item)}
                      className="border-zinc-700"
                    />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={handleMissingItemsSubmit}
              disabled={submitting || missingItems.length === 0}
              className="btn-primary w-full text-xs disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Trucks;
